import type { IAddArticleToCartCmd } from "@/data/commands/customer/AddArticleToCartCmd";
import type { IApplyDiscountToBillCmd } from "@/data/commands/customer/ApplyDiscountToBillCmd";
import type { IApplyDiscountToCartCmd } from "@/data/commands/customer/ApplyDiscountToCartCmd";
import type { ICheckoutBillCmd } from "@/data/commands/customer/CheckoutBillCmd";
import type { ICheckoutCartCmd } from "@/data/commands/customer/CheckoutCartCmd";
import type { ICustomerReceiveFulfillmentCmd } from "@/data/commands/customer/CustomerReceiveFulfillmentCmd";
import type { ICustomerSendReceiptCmd } from "@/data/commands/customer/CustomerSendReceiptCmd";
import type { IPrepareBillForCheckoutCmd } from "@/data/commands/customer/PrepareBillForCheckoutCmd";
import type { IPrepareCartForCheckoutCmd } from "@/data/commands/customer/PrepareCartForCheckoutCmd";
import type { IRemoveAllBillDiscountsCmd } from "@/data/commands/customer/RemoveAllBillDiscountsCmd";
import type { IRemoveAllCartDiscountsCmd } from "@/data/commands/customer/RemoveAllCartDiscountsCmd";
import type { IRemoveCartItemCommentCmd } from "@/data/commands/customer/RemoveCartItemCommentCmd";
import type { IRemoveCartItemDiscountsCmd } from "@/data/commands/customer/RemoveCartItemDiscountsCmd";
import type { IRemoveItemFromCartCmd } from "@/data/commands/customer/RemoveItemFromCartCmd";
import type { ISelectFulfillmentSlotCmd } from "@/data/commands/customer/SelectFulfillmentSlotCmd";
import type { ISetCartItemCommentCmd } from "@/data/commands/customer/SetCartItemCommentCmd";
import type { ISetCartItemQuantityCmd } from "@/data/commands/customer/SetCartItemQuantityCmd";
import type {
    IAllocateOperatorToTableCmd,
    IAllocateToTableByOrder,
    IAllocateToTableCmd,
    IAllocateToTableWithCodeCmd,
} from "@/data/commands/table/AllocateToTableCmd";
import type {
    IBill,
    IBillDiscount,
    IBillItem,
    IBillItemOption,
    IBillItemOptionGroup,
    IUserItemSelection,
} from "@/data/model/Bill";
import type {
    ICartItem,
    ICartItemOptionGroup,
    ICustomerArticle,
    ICustomerCart,
    ICustomerCategory,
    ICustomerFulfillment,
    ICustomerOffer,
    ICustomerOptionGroup,
    ICustomerOptionGroupOption,
    ICustomerOrder,
    IFulfillmentDateSlotDto,
    IFulfillmentTimeSlotDto,
    IFullCustomerOrder,
    IOrderPaymentConfirmation,
    IPaymentInformation,
    IPaymentIntent,
    IPaymentIntentConfirmHandle,
    IServicePaymentMethods,
    IWalleeTransactionLineToAuthorize,
    IWalleeTransactionToAuthorize,
    IWorldlineTransactionToAuthorize,
} from "@/data/model/Customer";
import type { ICustomerPayment, ICustomerPaymentItem } from "@/data/model/CustomerPayment";
import type { ITableAllocation } from "@/data/model/Table";
import type { IArticleOptionsQuery } from "@/data/queries/customer/ArticleOptionsQry";
import type { ICustomerOfferQry } from "@/data/queries/customer/CurrentOfferQry";
import type { ICustomerOrdersQry, IGetCurrentBillQry } from "@/data/queries/customer/CustomerOrdersQry";
import type { ICustomerPaymentsQry } from "@/data/queries/customer/CustomerPaymentsQry";
import type { IGetAvailableFulfillmentSlotsQry } from "@/data/queries/customer/GetAvailableFulfillmentSlotsQry";
import type { ICanOrderBeCanceledQry } from "@/data/queries/order/ICanOrderBeCanceledQry";
import { DateTime } from "@/data/util/DateTime";
import { Misc } from "@/data/util/Misc";

import {
    BaseServiceDao,
    type IPaginatedResult,
    parseCollection,
    parseMultiLanguageText,
    parsePaginatedResult,
} from "./BaseServiceDao";
import { LocationDao } from "./LocationDao";
import { OrderDao } from "./OrderDao";
import { PlanningDao } from "./PlanningDao";
import { RoomDao } from "./RoomDao";

export class CustomerDao extends BaseServiceDao {
    public static className = "CustomerDao";

    constructor() {
        super("customer");
    }

    public async getCurrentAllocation(): Promise<ITableAllocation> {
        return this.get("allocation", undefined, CustomerDao.parseTableAllocation);
    }

    public async allocateToTable(cmd: IAllocateToTableCmd): Promise<ITableAllocation> {
        return this.post("allocate", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateOperatorToTable(cmd: IAllocateOperatorToTableCmd): Promise<ITableAllocation> {
        return this.post("allocate-operator", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateToTableByOrderId(cmd: IAllocateToTableByOrder): Promise<ITableAllocation> {
        return this.post("allocate-by-order", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateToTableWithCode(cmd: IAllocateToTableWithCodeCmd): Promise<ITableAllocation> {
        return this.post("allocate/" + cmd.code, {}, CustomerDao.parseTableAllocation);
    }

    public async deallocateFromTable(): Promise<void> {
        return this.post("deallocate");
    }

    public async getOffer(qry: ICustomerOfferQry): Promise<ICustomerOffer> {
        return this.get("offer", qry, CustomerDao.parseCustomerOffer);
    }

    public async getCart(): Promise<ICustomerCart> {
        return this.get("cart", {}, CustomerDao.parseCustomerCart);
    }

    public async addArticleToCart(cmd: IAddArticleToCartCmd): Promise<ICustomerCart> {
        return this.post("cart/article", cmd, CustomerDao.parseCustomerCart);
    }

    public async setItemQuantityInCart(id: string, cmd: ISetCartItemQuantityCmd): Promise<ICustomerCart> {
        return this.put(`cart/item/${id}`, cmd, CustomerDao.parseCustomerCart);
    }

    public async removeItemFromCart(id: string, cmd: IRemoveItemFromCartCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}`, cmd, CustomerDao.parseCustomerCart);
    }

    public async setCartItemComment(id: string, cmd: ISetCartItemCommentCmd): Promise<ICustomerCart> {
        return this.put(`cart/item/${id}/comment`, cmd, CustomerDao.parseCustomerCart);
    }

    public async removeCartItemComment(id: string, cmd: IRemoveCartItemCommentCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}/comment`, cmd, CustomerDao.parseCustomerCart);
    }

    public async applyDiscountToCart(cmd: IApplyDiscountToCartCmd): Promise<ICustomerCart> {
        return this.post("cart/discount/apply", cmd, CustomerDao.parseCustomerCart);
    }

    public async removeAllCartDiscounts(cmd: IRemoveAllCartDiscountsCmd = {}): Promise<ICustomerCart> {
        return this.delete("cart/discount/remove-all", cmd, CustomerDao.parseCustomerCart);
    }

    public async removeCartItemDiscounts(id: string, cmd: IRemoveCartItemDiscountsCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}/discount`, cmd, CustomerDao.parseCustomerCart);
    }

    public async getOptionsOfArticle(qry: IArticleOptionsQuery): Promise<Array<ICustomerOptionGroup>> {
        return this.get(
            `article/${qry.article}/option`,
            {
                locationId: qry.locationId,
                offerId: qry.offerId,
                useCaseId: qry.useCaseId,
            },
            (c) => parseCollection(c, CustomerDao.parseCustomerOptionGroup)
        );
    }

    public async removeCartsFromLocation(): Promise<void> {
        return this.delete("cart/from-location");
    }

    public async prepareCartForCheckout(cmd: IPrepareCartForCheckoutCmd): Promise<IPaymentIntent> {
        return this.post("cart/prepare", cmd, CustomerDao.parseIntent);
    }

    public async getFulfillmentSlots(qry: IGetAvailableFulfillmentSlotsQry): Promise<Array<IFulfillmentDateSlotDto>> {
        return this.post("fulfillment-slots", qry, (c) => parseCollection(c, CustomerDao.parseFulfillmentDateSlot));
    }

    public async selectCartFulfillmentSlot(cmd: ISelectFulfillmentSlotCmd): Promise<void> {
        return this.post("cart/fulfillment-slot", cmd);
    }

    public async checkoutCart(cmd: ICheckoutCartCmd): Promise<IPaymentIntentConfirmHandle | undefined> {
        return this.post("cart/checkout", cmd, (c) =>
            c == null ? undefined : CustomerDao.parseIntentConfirmHandle(c)
        );
    }

    public async getCanOrderBeCanceled(orderId: string, cmd: ICanOrderBeCanceledQry): Promise<boolean> {
        return this.get("order/" + orderId + "/can-be-canceled", cmd);
    }

    public async sendReceipt(orderId: string, cmd: ICustomerSendReceiptCmd): Promise<void> {
        return this.post("order/" + orderId + "/receipt", cmd);
    }

    public async sendBillReceipt(paymentId: string, cmd: ICustomerSendReceiptCmd): Promise<void> {
        return this.post("payment/" + paymentId + "/receipt", cmd);
    }

    public async checkOrderPaymentConfirmation(transactionId: string): Promise<IOrderPaymentConfirmation> {
        return this.get("order/check/" + transactionId, undefined, CustomerDao.parseOrderPaymentConfirmation);
    }

    public async checkOrderPaymentConfirmationByExternalPaymentId(
        externalPaymentId: string
    ): Promise<IOrderPaymentConfirmation> {
        return this.get(
            "order/check-by-external-payment-id/" + externalPaymentId,
            undefined,
            CustomerDao.parseOrderPaymentConfirmation
        );
    }

    public async getOrder(id: string, abortSignal?: AbortSignal): Promise<IFullCustomerOrder> {
        return this.get("order/" + id, undefined, CustomerDao.parseFullCustomerOrder, abortSignal);
    }

    public async getOrders(qry: ICustomerOrdersQry): Promise<IPaginatedResult<ICustomerOrder>> {
        return this.get("order/", qry, (c) => parsePaginatedResult(c, CustomerDao.parseCustomerOrder));
    }

    public async getPayments(qry: ICustomerPaymentsQry): Promise<IPaginatedResult<ICustomerPayment>> {
        return this.get("payment/", qry, (c) => parsePaginatedResult(c, CustomerDao.parseCustomerPayment));
    }

    public async getActiveFulfillments(): Promise<Array<ICustomerFulfillment>> {
        return this.get("order/fulfillment", undefined, (c) =>
            parseCollection(c, CustomerDao.parseCustomerOrderFulfillment)
        );
    }

    public async receiveFulfillment(id: string, cmd: ICustomerReceiveFulfillmentCmd): Promise<void> {
        return this.post("order/fulfillment/" + id + "/received", cmd);
    }

    public async getCurrentBill(qry: IGetCurrentBillQry): Promise<IBill | undefined> {
        return this.get("bill", qry, CustomerDao.parseNullableBill);
    }

    public async applyDiscountToBill(cmd: IApplyDiscountToBillCmd): Promise<IBill> {
        return this.post("bill/discount/apply", cmd, CustomerDao.parseBill);
    }

    public async removeAllBillDiscounts(cmd: IRemoveAllBillDiscountsCmd): Promise<IBill> {
        return this.delete("bill/discount/remove-all", cmd, CustomerDao.parseBill);
    }

    public async prepareBillForCheckout(cmd: IPrepareBillForCheckoutCmd): Promise<IPaymentIntent> {
        return this.post("bill/prepare", cmd, CustomerDao.parseIntent);
    }

    public async checkoutBill(cmd: ICheckoutBillCmd): Promise<IPaymentIntentConfirmHandle | undefined> {
        return this.post("bill/checkout", cmd, (c) =>
            c == null ? undefined : CustomerDao.parseIntentConfirmHandle(c)
        );
    }

    public async getCustomerPayment(payment: string): Promise<ICustomerPayment> {
        return this.get("payment/" + payment, undefined, CustomerDao.parseCustomerPayment);
    }

    public async getCurrentIdentityId(): Promise<string | undefined> {
        return this.get("identity-id", undefined, (identityId) => (identityId == null ? undefined : identityId));
    }

    public async getServicePaymentMethods(serviceId: string): Promise<IServicePaymentMethods> {
        return this.get(`service/${serviceId}/payment-methods`, undefined, CustomerDao.parseServicePaymentMethods);
    }

    public static parseTableAllocation(literal: any): ITableAllocation {
        return {
            allocatedSince: DateTime.parseIsoDate(literal.allocatedSince),
            locationId: literal.locationId,
            locationInternalName: literal.locationInternalName == null ? undefined : literal.locationInternalName,
            locationDesign: literal.locationDesign == null ? {} : LocationDao.parseDesign(literal.locationDesign),
            tableId: literal.tableId,
            identityId: literal.identityId,
            tableNumber: literal.tableNumber,
            services: parseCollection(literal.services, RoomDao.parseService),
            locationName: literal.locationName,
            roomId: literal.roomId != null ? literal.roomId : undefined,
            roomName: literal.roomId != null ? literal.roomName : undefined,
            guestNumber: parseInt(literal.guestNumber),
            tableName: literal.tableName,
            tableSeats: parseInt(literal.tableSeats),
            country: parseInt(literal.country),
            customTranslations: literal.customTranslations != null ? literal.customTranslations : undefined,
            googleMapsLocationLink: literal.googleMapsLocationLink,
            locationAddress:
                literal.locationAddress == null ? undefined : LocationDao.parseAddress(literal.locationAddress),
            orderBillId: literal.orderBillId == null ? undefined : literal.orderBillId,
        };
    }

    private static parseCustomerOffer(literal: any): ICustomerOffer {
        return {
            locationId: literal.locationId,
            offerId: literal.offerId,
            cart: literal.cart == null ? undefined : CustomerDao.parseCustomerCart(literal.cart),
            entries: parseCollection(literal.entries, CustomerDao.parseCustomerCategory),
            // todo: this becomes required once we remove async options loading
            offerOptionGroups:
                literal.offerOptionGroups == null
                    ? undefined
                    : parseCollection(literal.offerOptionGroups, CustomerDao.parseCustomerOptionGroup),
        };
    }

    private static parseCustomerCategory(literal: any): ICustomerCategory {
        return {
            id: literal.id,
            categoryId: literal.categoryId,
            name: parseMultiLanguageText(literal.name),
            description: parseMultiLanguageText(literal.description),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            articles: parseCollection(literal.articles, CustomerDao.parseCustomerArticle),
        };
    }

    private static parseCustomerArticle(literal: any): ICustomerArticle {
        return {
            id: literal.id,
            articleId: literal.articleId,
            categoryId: literal.categoryId,
            offerId: literal.offerId,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            hasOptions: literal.hasOptions,
            shortDescription: parseMultiLanguageText(literal.shortDescription),
            longDescription: parseMultiLanguageText(literal.longDescription),
            price: literal.price == null ? undefined : parseFloat(literal.price),
            oohPrice: literal.oohPrice == null ? undefined : parseFloat(literal.oohPrice),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            originalImagePath: literal.originalImagePath == null ? undefined : literal.originalImagePath,
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
            isOpenPriceEnabled: literal.isOpenPriceEnabled,
            sortedOptionGroups: literal.sortedOptionGroups == null ? undefined : literal.sortedOptionGroups,
        };
    }

    public static parseCustomerCart(literal: any): ICustomerCart {
        return {
            cartId: literal.cartId,
            opened: DateTime.parseIsoDate(literal.opened),
            hash: literal.hash,
            total: parseFloat(literal.total),
            items: parseCollection(literal.items, CustomerDao.parseCartItem),
            serviceId: literal.serviceId,
            orderCartType: literal.orderCartType,
            preorderDate: literal.preorderDate == null ? undefined : DateTime.parseIsoDate(literal.preorderDate),
            preorderTime: literal.preorderTime == null ? undefined : literal.preorderTime,
            offerId: literal.offerId == null ? undefined : literal.offerId,
            itemDiscountsAmount: literal.itemDiscountsAmount,
            cartDiscountsAmount: literal.cartDiscountsAmount,
            vouchersAmount: literal.vouchersAmount,
            totalDiscountsAmount: literal.totalDiscountsAmount,
            totalDiscountsAndVouchersAmount: literal.totalDiscountsAndVouchersAmount,
            totalAfterDiscounts: literal.totalAfterDiscounts,
            totalAfterDiscountsAndVouchers: literal.totalAfterDiscountsAndVouchers,
            singleItemLevelDiscountItems: literal.singleItemLevelDiscountItems,
        };
    }

    private static parseCartItem(literal: any): ICartItem {
        return {
            id: literal.id,
            articleId: literal.articleId,
            added: DateTime.parseIsoDate(literal.added),
            quantity: parseInt(literal.quantity),
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            singlePrice: parseFloat(literal.singlePrice),
            hasDiscount: literal.hasDiscount,
            singlePriceAfterDiscount: parseFloat(literal.singlePriceAfterDiscount),
            minAge: literal.minAge == null ? undefined : parseInt(literal.minAge),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            options: parseCollection(literal.options, CustomerDao.parseCartOptionGroup),
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
            canHaveComments: literal.canHaveComments,
            comment: literal.comment == null ? undefined : literal.comment,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
        };
    }

    private static parseCartOptionGroup(literal: any): ICartItemOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            items:
                literal.items == null || literal.items.length === 0
                    ? []
                    : parseCollection(literal.items, CustomerDao.parseCartItem),
        };
    }

    private static parseIntent(literal: any): IPaymentIntent {
        return {
            javascriptUrl: literal.javascriptUrl,
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parsePaymentMethod),
            quickPaymentMethods: parseCollection(literal.quickPaymentMethods, PlanningDao.parsePaymentMethod),
        };
    }

    private static parseIntentConfirmHandle(literal: any): IPaymentIntentConfirmHandle {
        return {
            intentId: literal.intentId,
            intentSecret: literal.intentSecret,
            isAlreadyConfirmed: literal.isAlreadyConfirmed,
            walleeJavascriptUrl: literal.walleeJavascriptUrl == null ? undefined : literal.walleeJavascriptUrl,
            walleeOneClickUrl: literal.walleeOneClickUrl == null ? undefined : literal.walleeOneClickUrl,
            walleeSelectedPaymentId:
                literal.walleeSelectedPaymentId == null ? undefined : literal.walleeSelectedPaymentId,
            isPaymentDevice: literal.isPaymentDevice,
            openBill: literal.openBill == null ? undefined : literal.openBill,
            isWalleePerformTransactionSucceeded: literal.isWalleePerformTransactionSucceeded,
            walleeTransactionToAuthorize:
                literal.walleeTransactionToAuthorize == null
                    ? undefined
                    : CustomerDao.parseWalleeTransactionToAuthorize(literal.walleeTransactionToAuthorize),
            worldlineTransactionToAuthorize:
                literal.worldlineTransactionToAuthorize == null
                    ? undefined
                    : CustomerDao.parseWorldlineTransactionToAuthorize(literal.worldlineTransactionToAuthorize),
            wasPaymentCoveredByDiscounts: literal.wasPaymentCoveredByDiscounts,
        };
    }

    private static parseWorldlineTransactionToAuthorize(literal: any): IWorldlineTransactionToAuthorize {
        return {
            externalPaymentId: literal.externalPaymentId,
            currency: literal.currency,
            amount: parseFloat(literal.amount),
            sessionId: literal.sessionId,
        };
    }
    private static parseWalleeTransactionToAuthorize(literal: any): IWalleeTransactionToAuthorize {
        return {
            externalPaymentId: literal.externalPaymentId,
            currency: literal.currency,
            lines: parseCollection(literal.lines, CustomerDao.parseWalleeTransactionLineToAuthorize),
        };
    }

    private static parseWalleeTransactionLineToAuthorize(literal: any): IWalleeTransactionLineToAuthorize {
        return {
            paymentItemId: literal.paymentItemId,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseOrderPaymentConfirmation(literal: any): IOrderPaymentConfirmation {
        return {
            error: literal.error == null ? undefined : literal.error,
            paymentFor: parseInt(literal.paymentFor),
            currency: parseInt(literal.currency),
            amount: parseFloat(literal.amount),
            isConfirmed: literal.isConfirmed == null ? undefined : literal.isConfirmed,
            orderNumber: literal.orderNumber == null ? undefined : literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            tableName: literal.tableName == null ? undefined : literal.tableName,
            orderId: literal.orderId == null ? undefined : literal.orderId,
            paymentMethodSplitInformation: parseCollection(
                literal.paymentMethodSplitInformation,
                CustomerDao.parsePaymentInformation
            ),
            orderServiceId: literal.orderServiceId == null ? undefined : literal.orderServiceId,
            paymentId: literal.paymentId == null ? undefined : literal.paymentId,
            scheduledFulfillmentDateTime:
                literal.scheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            billId: literal.billId == null ? undefined : literal.billId,
            sendEmailConfirmationsForUnpaidOrders: literal.sendEmailConfirmationsForUnpaidOrders,
            isPayLater: literal.isPayLater,
            payUseCaseId: literal.payUseCaseId == null ? undefined : literal.payUseCaseId,
            orderUseCaseId: literal.orderUseCaseId == null ? undefined : literal.orderUseCaseId,
            providerId: literal.providerId == null ? undefined : literal.providerId,
        };
    }

    private static parsePaymentInformation(literal: any): IPaymentInformation {
        return {
            id: literal.id,
            amount: parseFloat(literal.amount),
            type: literal.type == null ? undefined : literal.type,
            brand: literal.brand == null ? undefined : literal.brand,
            ccDigit: literal.ccDigit == null ? undefined : literal.ccDigit,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            isCardScheme: literal.isCardScheme,
            actualPaymentMethodIconUrl:
                literal.actualPaymentMethodIconUrl == null ? undefined : literal.actualPaymentMethodIconUrl,
            actualPaymentMethodName:
                literal.actualPaymentMethodName == null ? undefined : literal.actualPaymentMethodName,
            predefinedPaymentMethodIconUrl:
                literal.predefinedPaymentMethodIconUrl == null ? undefined : literal.predefinedPaymentMethodIconUrl,
            predefinedOnlinePaymentMethodName:
                literal.predefinedOnlinePaymentMethodName == null
                    ? undefined
                    : literal.predefinedOnlinePaymentMethodName,
            customPaymentMethodName:
                literal.customPaymentMethodName == null ? undefined : literal.customPaymentMethodName,
            customPaymentMethodIcon:
                literal.customPaymentMethodIcon == null ? undefined : literal.customPaymentMethodIcon,
        };
    }

    private static parseCustomerOrder(literal: any): ICustomerOrder {
        return {
            id: literal.id,
            orderNumber: literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            consumption: parseFloat(literal.consumption),
            tip: parseFloat(literal.tip),
            date: DateTime.parseIsoDate(literal.date),
            locationName: literal.locationName,
            logoPath: literal.logoPath == null ? undefined : literal.logoPath,
            tableName: literal.tableName,
            orderServiceId: literal.orderServiceId,
            state: parseInt(literal.state),
            preorder: literal.preorder,
            effectiveFulfillmentReadyDateTime:
                literal.effectiveFulfillmentReadyDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveFulfillmentReadyDateTime),
            payLater: literal.payLater,
            scheduledFulfillmentDateTime:
                literal.scheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
        };
    }

    private static parseFullCustomerOrder(literal: any): IFullCustomerOrder {
        return {
            ...CustomerDao.parseCustomerOrder(literal),
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
            currentOrderDiscountAmount: parseFloat(literal.currentOrderDiscountAmount),
            isTipCancelled: literal.isTipCancelled,
            totalNetAmountFromPaymentsDrafts: parseFloat(literal.totalNetAmountFromPaymentsDrafts),
            totalPaymentAmountFromPaymentDrafts: parseFloat(literal.totalPaymentAmountFromPaymentDrafts),
            totalPaidAmount: parseFloat(literal.totalPaidAmount),
            totalCancelledAmount: parseFloat(literal.totalCancelledAmount),
        };
    }

    private static parseCustomerOrderFulfillment(literal: any): ICustomerFulfillment {
        return {
            id: literal.id,
            orderId: literal.orderId,
            orderDate: DateTime.parseIsoDate(literal.orderDate),
            orderNumber: literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            locationName: literal.locationName,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            articles: parseInt(literal.articles),
            orderMainArticleQty: parseInt(literal.orderMainArticleQty),
            name: literal.name == null ? undefined : parseMultiLanguageText(literal.name),
            effectiveReadyDateTime: DateTime.parseIsoDate(literal.effectiveReadyDateTime),
            handoutDate: literal.handoutDate == null ? undefined : DateTime.parseIsoDate(literal.handoutDate),
            isFulfilled: literal.isFulfilled,
            tableName: literal.tableName,
            orderServiceId: literal.orderServiceId,
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            state: parseInt(literal.state),
            currentQueuePosition:
                literal.currentQueuePosition == null ? undefined : parseInt(literal.currentQueuePosition),
            fulfillmentGroupOrder:
                literal.fulfillmentGroupOrder == null ? undefined : parseInt(literal.fulfillmentGroupOrder),
            preorder: literal.preorder,
            hideCurrentQueueIndicator: literal.hideCurrentQueueIndicator,
            isLinked: literal.isLinked,
            orderScheduledFulfillmentDateTime:
                literal.orderScheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.orderScheduledFulfillmentDateTime),
        };
    }

    private static parseCustomerOptionGroup(literal: any): ICustomerOptionGroup {
        return {
            optionGroupId: literal.optionGroupId,
            displayName: literal.displayName,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            minOptions: literal.minOptions == null ? undefined : parseInt(literal.minOptions),
            maxOptions: literal.maxOptions == null ? undefined : parseInt(literal.maxOptions),
            maxPerOption: literal.maxPerOption == null ? undefined : parseInt(literal.maxPerOption),
            maxSum: literal.maxSum == null ? undefined : parseInt(literal.maxSum),
            options: parseCollection(literal.options, CustomerDao.parseCustomerOptionGroupOption),
        };
    }

    private static parseCustomerOptionGroupOption(literal: any): ICustomerOptionGroupOption {
        return {
            articleId: literal.articleId,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            optionGroups: parseCollection(literal.optionGroups, CustomerDao.parseCustomerOptionGroup),
            price: literal.price == null ? undefined : parseFloat(literal.price),
            oohPrice: literal.oohPrice == null ? undefined : parseFloat(literal.oohPrice),
            preselected: literal.preselected,
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
        };
    }

    public static parseBill(literal: any): IBill {
        return {
            id: literal.id,
            tableId: literal.tableId,
            opened: DateTime.parseIsoDate(literal.opened),
            closed: literal.closed != null ? DateTime.parseIsoDate(literal.closed) : undefined,
            total: Misc.parseFloat(literal.total)!,
            totalPaid: Misc.parseFloat(literal.totalPaid)!,
            unpaid: Misc.parseFloat(literal.unpaid)!,
            items: parseCollection(literal.items, CustomerDao.parseBillItem),
            paidItems: parseCollection(literal.paidItems, CustomerDao.parseBillItem),
            selections: parseCollection(literal.selections, CustomerDao.parseUserItemSelection),
            customName: literal.customName == null ? undefined : literal.customName,
            type: parseInt(literal.type),
            appliedDiscounts: parseCollection(literal.appliedDiscounts, CustomerDao.parseBillDiscount),
            discountAmount: parseFloat(literal.discountAmount),
            manualEndUsersCount:
                literal.manualEndUsersCount == null ? undefined : parseInt(literal.manualEndUsersCount),
        };
    }

    public static parseNullableBill(literal: any): IBill | undefined {
        if (literal === undefined || literal == null) return undefined;

        return CustomerDao.parseBill(literal);
    }

    private static parseBillItem(literal: any): IBillItem {
        return {
            id: literal.id,
            orderId: literal.orderId,
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            comment: literal.comment != null ? OrderDao.parseOrderItemComment(literal.comment) : undefined,
            quantity: parseInt(literal.quantity),
            paidQuantity: parseInt(literal.paidQuantity),
            unpaidQuantity: parseInt(literal.unpaidQuantity),
            paidAmount: Misc.parseFloat(literal.paidAmount)!,
            unpaidAmount: Misc.parseFloat(literal.unpaidAmount)!,
            price: Misc.parseFloat(literal.price)!,
            priceWithOptions: Misc.parseFloat(literal.priceWithOptions)!,
            isZeroPriceItem: literal.isZeroPriceItem,
            sortOrder: parseInt(literal.sortOrder),
            options: parseCollection(literal.options, CustomerDao.parseBillItemOptionGroup),
        };
    }

    private static parseBillDiscount(literal: any): IBillDiscount {
        return {
            id: literal.id,
            voucherId: literal.voucherId == null ? undefined : literal.voucherId,
            discountId: literal.discountId == null ? undefined : literal.discountId,
            identityId: literal.identityId,
            amount: parseFloat(literal.amount),
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
        };
    }

    private static parseBillItemOptionGroup(literal: any): IBillItemOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            items: parseCollection(literal.items, CustomerDao.parseBillItemOption),
        };
    }

    private static parseBillItemOption(literal: any): IBillItemOption {
        return {
            id: literal.id,
            quantity: parseFloat(literal.quantity),
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            price: Misc.parseFloat(literal.price)!,
            options: parseCollection(literal.options, CustomerDao.parseBillItemOptionGroup),
        };
    }

    private static parseUserItemSelection(literal: any): IUserItemSelection {
        return {
            lineItemId: literal.lineItemId,
            quantity: parseInt(literal.quantity),
            identityId: literal.identityId,
        };
    }

    private static parseCustomerPayment(literal: any): ICustomerPayment {
        return {
            ...OrderDao.parsePayment(literal),
            userId: literal.userId == null ? undefined : literal.userId,
            userName: literal.userName == null ? undefined : literal.userName,
            locationId: literal.locationId == null ? undefined : literal.locationId,
            locationName: literal.locationName == null ? undefined : literal.locationName,
            locationLogoPath: literal.locationLogoPath == null ? undefined : literal.locationLogoPath,
            items: parseCollection(literal.items, CustomerDao.parseCustomerPaymentItem),
            vatGroups: parseCollection(literal.vatGroups, OrderDao.parseVatGroupDto),
            consumption: parseFloat(literal.consumption),
            orderLevelDiscountAmount: parseFloat(literal.orderLevelDiscountAmount),
            totalPaid: parseFloat(literal.totalPaid),
        };
    }

    private static parseCustomerPaymentItem(literal: any): ICustomerPaymentItem {
        return {
            amount: parseFloat(literal.amount),
            id: literal.id,
            orderItem: OrderDao.parseOrderItem(literal.orderItem),
            quantity: parseInt(literal.quantity),
        };
    }

    static parseFulfillmentDateSlot(literal: any): IFulfillmentDateSlotDto {
        return {
            locationId: literal.locationId,
            date: DateTime.parseIsoDate(literal.date),
            timeSlots: parseCollection(literal.timeSlots, CustomerDao.parseFulfillmentTimeSlot),
            isAvailable: literal.isAvailable,
        };
    }

    static parseFulfillmentTimeSlot(literal: any): IFulfillmentTimeSlotDto {
        return {
            startTime: literal.startTime,
            endTime: literal.endTime,
            offerId: literal.offerId,
            availableCapacity: literal.availableCapacity == null ? undefined : parseInt(literal.availableCapacity),
            isAvailable: literal.isAvailable,
        };
    }

    static parseServicePaymentMethods(literal: any): IServicePaymentMethods {
        return {
            serviceId: literal.serviceId,
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parsePaymentMethod),
            quickPaymentMethods: parseCollection(literal.quickPaymentMethods, PlanningDao.parsePaymentMethod),
        };
    }
}
